import { Avatar, AvatarImage } from "@/components/ui/avatar";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import {
  CREDITS_RETRIEVAL_COST,
  useCredits,
} from "@/contexts/credits-provider";
import { useTeams } from "@/contexts/teams-provider";
import { ThemeProviderContext } from "@/contexts/theme-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { useAdminModeCountdown } from "@/hooks/useAdminModeCountdown";
import { useClerk, useUser } from "@clerk/react";
import {
  CheckIcon,
  InfoIcon,
  MoonIcon,
  SunIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useContext } from "react";
import { LuComputer } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

export function NavUserDropdown() {
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { isAdmin, isGod, isManager, isBilling, teamSelected } = useTeams();
  const { t } = useTranslation();
  const { creditsBalance } = useCredits();

  const themeContext = useContext(ThemeProviderContext);
  const { isAdminMode, setAdminMode, isGodModeEnabled, setGodModeEnabled } =
    useAdminMode();
  const adminModeCountdown = useAdminModeCountdown();

  return (
    <Menubar className="border-0">
      <MenubarMenu>
        <MenubarTrigger className="relative p-0 overflow-hidden rounded-full cursor-pointer">
          <Avatar className="w-8 h-8">
            <AvatarImage
              src={user?.imageUrl}
              alt={user?.fullName || ""}
              className="object-cover"
            />
          </Avatar>
        </MenubarTrigger>
        <MenubarContent>
          <MenubarItem disabled className="data-disabled:opacity-100">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {user?.fullName}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
          </MenubarItem>
          {!teamSelected?.setupTeamCreation && (
            <>
              <MenubarSeparator />
              {teamSelected?.userLicence && (
                <MenubarItem className="focus:bg-transparent">
                  <div className="flex flex-row items-center justify-between w-full text-xs">
                    <span className="font-medium text-left">
                      {teamSelected.userLicence === "professional"
                        ? t(`userNav.licence.professional`)
                        : t(`userNav.licence.basic`)}
                    </span>
                    {teamSelected.userLicence === "professional" && (
                      <span className="flex items-center gap-2 text-right text-muted-foreground">
                        {t("userNav.licence.unlimited")}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon className="w-4 h-4 cursor-pointer" />
                          </TooltipTrigger>
                          <TooltipContent>
                            {teamSelected.userLicence === "professional"
                              ? t("userNav.licence.tooltip.licenceProfessional")
                              : t("userNav.licence.tooltip.licenceBasic")}
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    )}
                  </div>
                </MenubarItem>
              )}
              <MenubarItem className="focus:bg-transparent">
                <div className="flex flex-row items-center justify-between w-full text-xs">
                  <span className="font-medium text-left">
                    {t(`userNav.credits.name`)}
                  </span>
                  <span
                    className={`text-xs text-right flex items-center gap-2 ${
                      creditsBalance === 0
                        ? "text-warning"
                        : "text-muted-foreground"
                    }`}
                  >
                    {creditsBalance}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {creditsBalance === 0 ? (
                          <TriangleAlertIcon
                            className={`w-4 h-4 cursor-pointer`}
                            onClick={
                              isManager
                                ? () => navigate("/team/credits")
                                : undefined
                            }
                          />
                        ) : (
                          <InfoIcon className="w-4 h-4 cursor-pointer" />
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        {!isManager && creditsBalance === 0
                          ? t("userNav.noCredits.tooltip")
                          : t("userNav.credits.tooltip", {
                              phonesCount: Math.floor(
                                creditsBalance / CREDITS_RETRIEVAL_COST.PHONE
                              ),
                            })}
                      </TooltipContent>
                    </Tooltip>
                  </span>
                </div>
              </MenubarItem>
            </>
          )}
          <MenubarSeparator />
          <MenubarItem onClick={() => openUserProfile()}>
            {t("userNav.account")}
          </MenubarItem>
          {!isBilling && (
            <MenubarItem onClick={() => navigate(`/settings`)}>
              {t("userNav.settings")}
            </MenubarItem>
          )}
          <MenubarItem onClick={() => navigate(`/integrations`)}>
            {t("userNav.integrations")}
          </MenubarItem>
          {(isAdmin || isManager) && (
            <MenubarItem onClick={() => navigate(`/team/billing`)}>
              {t("userNav.billing")}
              {teamSelected?.paymentMethodMissed === true && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <TriangleAlertIcon className="w-4 h-4 ml-auto text-warning" />
                  </TooltipTrigger>
                  <TooltipContent>{t("userNav.billingWarning")}</TooltipContent>
                </Tooltip>
              )}
            </MenubarItem>
          )}
          <MenubarItem onClick={() => navigate(`/referrals`)}>
            {t("userNav.referral")}
          </MenubarItem>
          <MenubarSeparator />
          <MenubarSub>
            <MenubarSubTrigger>{t("userNav.theme.title")}</MenubarSubTrigger>
            <MenubarSubContent>
              <MenubarItem onClick={() => themeContext.setTheme("system")}>
                <LuComputer className="w-3 h-3 mr-2" />
                {t("userNav.theme.system")}
                <CheckIcon
                  className={cn(
                    "ml-3 h-4 w-4",
                    themeContext.theme === "system"
                      ? "opacity-100"
                      : "opacity-0"
                  )}
                />
              </MenubarItem>
              <MenubarItem onClick={() => themeContext.setTheme("light")}>
                <SunIcon className="w-3 h-3 mr-2" />
                {t("userNav.theme.light")}
                <CheckIcon
                  className={cn(
                    "ml-auto h-4 w-4",
                    themeContext.theme === "light" ? "opacity-100" : "opacity-0"
                  )}
                />
              </MenubarItem>
              <MenubarItem onClick={() => themeContext.setTheme("dark")}>
                <MoonIcon className="w-3 h-3 mr-2" />
                {t("userNav.theme.dark")}
                <CheckIcon
                  className={cn(
                    "ml-auto h-4 w-4",
                    themeContext.theme === "dark" ? "opacity-100" : "opacity-0"
                  )}
                />
              </MenubarItem>
            </MenubarSubContent>
          </MenubarSub>
          <MenubarSeparator />
          {isAdmin && (
            <>
              <MenubarItem
                onClick={(e) => e.preventDefault()}
                className="bg-warning-foreground"
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex flex-col">
                    <span>{t("userNav.admin.mode")}</span>
                    {isAdminMode && adminModeCountdown && (
                      <span className="text-[10px] text-muted-foreground">
                        {adminModeCountdown}
                      </span>
                    )}
                  </div>
                  <Switch
                    className="ml-auto"
                    checked={isAdminMode}
                    onCheckedChange={() => setAdminMode(!isAdminMode)}
                  />
                </div>
              </MenubarItem>
              {isAdminMode && isGod && (
                <MenubarItem
                  onClick={(e) => e.preventDefault()}
                  className="bg-warning-foreground"
                >
                  {t("userNav.admin.godMode")}{" "}
                  <Switch
                    className="ml-auto"
                    checked={isGodModeEnabled}
                    onCheckedChange={() =>
                      setGodModeEnabled(!isGodModeEnabled)
                    }
                  />
                </MenubarItem>
              )}
              {isAdminMode && (
                <MenubarItem
                  onClick={() => navigate(`/administration`)}
                  className="bg-warning-foreground"
                >
                  {t("userNav.admin.title")}
                </MenubarItem>
              )}
              <MenubarSeparator />
            </>
          )}
          <MenubarItem onClick={() => { posthog?.reset(); signOut(); }}>
            {t("userNav.logout")}
          </MenubarItem>
        </MenubarContent>
      </MenubarMenu>
    </Menubar>
  );
}
